import { Component, AfterViewInit, ViewChild, ElementRef, PLATFORM_ID, Inject, HostListener } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { VisitorService } from '../../../../services/VisitTracker/visit-tracker.service';
import { StatisticsService } from '../../../../services/Statistics/statistics.service';
import { combineLatest } from 'rxjs';
import { NotificationService } from '../../../../services/notification/notification.service';
import { NotificationRequest } from '../../../../models/notification-request';
import { AuthServiceService } from '../../../../services/authService/auth-service.service';
import { RouterLink } from '@angular/router';
import { SharedService } from '../../../../services/shared/shared.service';

@Component({
  imports: [CommonModule, RouterLink],
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements AfterViewInit {
  totalUsers = 0;
  totalVisitors = 0;
  totalArticles = 0;
  totalApplications = 0;
  userUid: any;
  @ViewChild('monthlyChart') monthlyChartRef!: ElementRef;
  private monthlyChartInstance: any;

  selectedPeriod: 'day' | 'month' = 'day'; // default period
  notifications = new Array<NotificationRequest>();

  constructor(
    private visitorService: VisitorService,
    private statisticsService: StatisticsService,
    private notificationService: NotificationService,
    private authService: AuthServiceService,
    public sharedService: SharedService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    // Load totals
    this.statisticsService.getCllectionCount("users").subscribe(count => this.totalUsers = count);
    this.statisticsService.getCllectionCount("applications").subscribe(count => this.totalApplications = count);
    this.statisticsService.getCllectionCount("articles").subscribe(count => this.totalArticles = count);
    this.statisticsService.getTotalVisitors().subscribe(count => this.totalVisitors = count);
    this.userUid = this.authService.getCurrentUserUid();

    this.getNotifications(this.userUid);
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadMonthlyVisitors(this.selectedPeriod);
    }
  }

  async loadMonthlyVisitors(period: 'day' | 'month') {
    const ApexCharts = (await import('apexcharts')).default;
    this.selectedPeriod = period;

    combineLatest([
      this.visitorService.getVisitors(period),
      this.statisticsService.getUserRegistrations(period)
    ]).subscribe(([visitors, users]) => {
      // Normalize dates to YYYY-MM-DD
      function normalizeDate(label: string): string {
        if (label.includes('/')) {
          const [day, month, year] = label.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return label; // already ISO format
      }

      // Normalize and aggregate visitors
      const visitorCountsByDate: { [key: string]: number } = {};
      visitors.forEach((v: any) => {
        const date = normalizeDate(v.label);
        if (!visitorCountsByDate[date]) visitorCountsByDate[date] = 0;
        visitorCountsByDate[date] += v.count;
      });

      // Normalize and aggregate users
      const userCountsByDate: { [key: string]: number } = {};
      users.forEach((u: any) => {
        const date = normalizeDate(u.label);
        if (!userCountsByDate[date]) userCountsByDate[date] = 0;
        userCountsByDate[date] += u.count;
      });

      // Build full sorted date range (union of both)
      const allDates = Array.from(new Set([
        ...Object.keys(visitorCountsByDate),
        ...Object.keys(userCountsByDate)
      ])).sort();

      // Align series data
      const visitorCounts = allDates.map(date => visitorCountsByDate[date] ?? 0);
      const userCounts = allDates.map(date => userCountsByDate[date] ?? 0);

      const options = {
        chart: { type: 'line', height: 350, toolbar: { show: true } },
        stroke: { curve: 'smooth', width: 3 },
        series: [
          { name: 'Besucher', data: visitorCounts },
          { name: 'Benutzer', data: userCounts }
        ],
        xaxis: { categories: allDates, title: { text: period === 'day' ? 'Tage' : 'Monate' } },
        yaxis: { title: { text: 'Anzahl' } },
        colors: ['#00a65a', '#007bff'],
        title: { text: 'Statistiken zu Besuchern und Benutzern', align: 'left' },
        tooltip: { enabled: true },
        legend: { show: true, position: 'top' }
      };

      if (this.monthlyChartInstance) {
        this.monthlyChartInstance.updateOptions(options, true, true);
      } else {
        this.monthlyChartInstance = new ApexCharts(this.monthlyChartRef.nativeElement, options);
        this.monthlyChartInstance.render();
      }
    });
  }

  getNotifications(userUid: string) {
    this.notificationService.getNotificationsByUserUid(userUid).subscribe({
      next: res => {
        this.notifications = res;
        if (this.notifications.length > 5) {
          this.notifications = this.notifications.slice(0, 5);
        }
      },
    });
  }

  updateAllNotifications(userUid: string) {
    this.notificationService.updateNotificationsToNotNew(userUid).subscribe({
      next: () => {
        console.log('Alle neuen Benachrichtigungen auf "nicht neu" aktualisiert');
      },
      error: (err) => {
        console.error('Fehler beim Aktualisieren der Benachrichtigungen:', err);
      }
    });
  }

  toggleDropdown(notification: any) {
    this.notifications.forEach(n => n.isOpen = n === notification ? !n.isOpen : false);
  }

  markAsRead(notification: any) {
    notification.status = 'READ';
    notification.isOpen = false;
    this.notificationService.markNotificationAsRead(this.userUid, notification.id);
  }

  deleteNotification(notification: any) {
    this.notificationService.deleteNotification(this.userUid, notification.id);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.position-relative')) {
      this.notifications.forEach(n => n.isOpen = false);
    }
  }
}